import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { WithdrawalService } from '../../_core/_services/withdrawal.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { AuthService } from '../../_core/_services/auth.service';
import { prettifyFieldKey, maskSensitiveValue } from '../../_core/_helpers/withdrawal-field.helper';
import {
  WithdrawalPreviewPayload
} from '../../_core/_model/withdrawal';

@Component({
  selector: 'app-withdrawal-confirm',
  standalone: false,
  templateUrl: './withdrawal-confirm.html',
  styleUrl: './withdrawal-confirm.css'
})
export class WithdrawalConfirm implements OnInit {
  walletId = '';
  payload: WithdrawalPreviewPayload | null = null;
  submitting = false;
  securityLoading = true;
  requiresWithdrawalPin = false;

  showWithdrawalPinModal = false;
  accountPin = '';
  withdrawalPinError = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private withdrawalService: WithdrawalService,
    private notifyService: NotificationService,
    private auth: AuthService
  ) { }

  ngOnInit(): void {
    this.walletId = this.route.snapshot.paramMap.get('walletId') || '';

    this.auth.getCurrentUser().subscribe({
      next: user => {
        this.requiresWithdrawalPin = !!user?.accountPin?.requirePinForWithdrawals;
        this.securityLoading = false;
      },
      error: () => {
        this.securityLoading = false;
      },
      complete: () => {
        this.securityLoading = false;
      }
    });

    const raw = sessionStorage.getItem('withdrawal_preview');

    if (!raw) {
      this.notifyService.showError('No withdrawal draft found.', 'Error');
      this.router.navigate(['/wallets/withdrawal', this.walletId]);
      return;
    }

    try {
      this.payload = JSON.parse(raw) as WithdrawalPreviewPayload;
    } catch {
      this.notifyService.showError('Withdrawal draft is invalid.', 'Error');
      this.router.navigate(['/wallets/withdrawal', this.walletId]);
    }
  }

  get destinationEntries(): { key: string; label: string; value: any }[] {
    if (!this.payload?.destinationDetails) return [];

    return Object.entries(this.payload.destinationDetails).map(([key, value]) => ({
      key,
      label: prettifyFieldKey(key),
      value: maskSensitiveValue(key, value)
    }));
  }

  get resolutionEntries(): { key: string; value: string | boolean }[] {
    if (!this.payload?.resolutionResult) return [];

    return Object.entries(this.payload.resolutionResult)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key,
        value: value as string | boolean
      }));
  }

  confirm(): void {
    if (this.submitting || !this.payload) {
      return;
    }

    if (this.securityLoading) {
      this.notifyService.showInfo('Account security settings are still loading.', 'Withdrawal');
      return;
    }

    if (this.requiresWithdrawalPin) {
      this.accountPin = '';
      this.withdrawalPinError = '';
      this.showWithdrawalPinModal = true;
      return;
    }

    this.submitWithdrawal(null);
  }

  authorizeWithdrawal(pin: string): void {
    this.accountPin = pin;
    this.withdrawalPinError = '';
    this.submitWithdrawal(pin);
  }

  closeWithdrawalPinModal(): void {
    if (this.submitting) {
      return;
    }

    this.showWithdrawalPinModal = false;
    this.accountPin = '';
    this.withdrawalPinError = '';
  }

  back(): void {
    this.router.navigate(['/wallets/withdraw', this.walletId]);
  }

  pretty(key: string): string {
    return prettifyFieldKey(key);
  }

  private submitWithdrawal(accountPin: string | null): void {
    if (this.submitting || !this.payload) {
      return;
    }

    this.submitting = true;

    const request = {
      walletId: this.payload.walletId,
      amount: this.payload.amount,
      paymentMethod: this.payload.paymentMethod,
      destinationDetailsJson: JSON.stringify(this.payload.destinationDetails),
      idempotencyKey: this.generateIdempotencyKey(),
      accountPin
    };

    this.withdrawalService.initiateWithdrawal(request).subscribe({
      next: (res) => {
        const data = res.data;

        this.showWithdrawalPinModal = false;
        this.accountPin = '';
        this.withdrawalPinError = '';

        const finishNavigation = () => {
          sessionStorage.removeItem('withdrawal_preview');
          this.notifyService.showSuccess('Withdrawal initiated successfully.', 'Success');

          this.router.navigate(['/wallets/withdraw/history'], {
            queryParams: { ref: data?.externalReference || '' }
          });
        };

        if (this.payload?.saveBeneficiary) {
          this.withdrawalService.createBeneficiary({
            walletId: this.payload.walletId,
            nickname: this.payload.beneficiaryNickname,
            paymentMethod: this.payload.paymentMethod,
            destinationDetailsJson: JSON.stringify(this.payload.destinationDetails)
          }).subscribe({
            next: () => {
              this.notifyService.showSuccess('Beneficiary saved successfully.', 'Success');
              finishNavigation();
            },
            error: (err) => {
              const message =
                err?.error?.Error?.Details ||
                err?.error?.message ||
                'Withdrawal was initiated, but beneficiary could not be saved.';

              this.notifyService.showError(message, 'Beneficiary Error');
              finishNavigation();
            },
            complete: () => {
              this.submitting = false;
            }
          });
        } else {
          finishNavigation();
          this.submitting = false;
        }
      },
      error: (err) => {
        const message =
          err?.error?.Error?.Details ||
          err?.error?.message ||
          'Failed to initiate withdrawal';

        if (this.showWithdrawalPinModal) {
          this.withdrawalPinError = message;
          this.accountPin = '';
        }

        this.notifyService.showError(message, 'Error');
        this.submitting = false;
      }
    });
  }

  private generateIdempotencyKey(): string {
    return `wdr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}
